import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { ChevronDown, LogOut, Settings, Sparkles, User } from 'lucide-react';

import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { api } from '@/lib/api';
import { ROLE_COLORS, ROLE_LABELS, getInitials } from '@/lib/role-display';
import { cn } from '@/lib/utils';
import { useAuth } from '@/store/auth';

// Réponse de GET /tenants/me (tenants-service) : projection réduite pour tous les membres (nom, slug, branding,
// statut) ; coordonnées, TVA, adresse et fin d'essai seulement avec tenants.read.
export interface TenantMe {
  name: string;
  slug: string;
  status: string;
  brandingLogoKey: string | null;
  brandingColor: string | null;
  // 8.6 : URL présignée (1 h) du logo, dans les deux projections ; absente d'un cache antérieur
  brandingLogoUrl?: string | null;
  contactEmail?: string;
  contactPhone?: string | null;
  vatNumber?: string | null;
  address?: string | null;
  trialEndsAt?: string | null;
}

export const useTenantMe = () =>
  useQuery({
    queryKey: ['tenant-me'],
    queryFn: async () => (await api.get<TenantMe>('/tenants/me')).data,
    staleTime: 5 * 60_000,
    refetchOnWindowFocus: false,
  });

export const TopBar = () => {
  const session = useAuth((s) => s.session);
  const logout = useAuth((s) => s.logout);
  const queryClient = useQueryClient();
  const { data: tenant } = useTenantMe();
  // URL expirée ou objet absent : le logo est masqué plutôt qu'affiché cassé
  const [brokenLogo, setBrokenLogo] = useState<string | null>(null);
  const logoUrl = tenant?.brandingLogoUrl && tenant.brandingLogoUrl !== brokenLogo ? tenant.brandingLogoUrl : null;

  const user = session?.user;
  const fullName = user ? `${user.firstName} ${user.lastName}` : '';

  return (
    <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between gap-4 border-b bg-white px-4">
      <div className="flex min-w-0 items-center gap-2">
        <img src="/favicon.svg" alt="" className="h-7 w-7" />
        <span className="font-bold tracking-tight">
          <span className="text-brand-violet">LOGISTECH</span>
          <span className="text-brand-teal">-LGT</span>
        </span>
        {(logoUrl || tenant?.name) && (
          <span className="flex min-w-0 items-center gap-2 border-l pl-2">
            {logoUrl && (
              <img src={logoUrl} alt={`Logo ${tenant?.name ?? ''}`.trim()} className="h-7 max-w-[7rem] object-contain"
                onError={() => setBrokenLogo(logoUrl)} />
            )}
            {tenant?.name && <span className="truncate text-sm text-muted-foreground">{tenant.name}</span>}
          </span>
        )}
      </div>

      {user && (
        <DropdownMenu>
          <DropdownMenuTrigger
            className="flex items-center gap-2 rounded-md px-2 py-1 hover:bg-muted focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            aria-label="Menu utilisateur"
          >
            <Avatar fallback={getInitials(user.firstName, user.lastName)} />
            <span className="hidden text-sm font-medium sm:inline">{fullName}</span>
            {/* Custom role : son nom, avec la couleur de son rang built-in */}
            <Badge
              className={cn('hidden md:inline-flex gap-1', ROLE_COLORS[user.role])}
              title={user.customRoleName ? `Rôle personnalisé, basé sur ${ROLE_LABELS[user.role]}` : undefined}
            >
              {user.customRoleName && <Sparkles className="h-3 w-3" aria-hidden />}
              {user.customRoleName ?? ROLE_LABELS[user.role]}
            </Badge>
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <div className="truncate text-muted-foreground">{user.email}</div>
              <div className="mt-1 truncate text-xs">
                {user.customRoleName
                  ? <>{user.customRoleName} <span className="text-muted-foreground">· Personnalisé, basé sur {ROLE_LABELS[user.role]}</span></>
                  : ROLE_LABELS[user.role]}
              </div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem asChild>
              <Link to="/profile" className="gap-2"><User className="h-4 w-4" /> Mon profil</Link>
            </DropdownMenuItem>
            <DropdownMenuItem asChild>
              <Link to="/settings" className="gap-2"><Settings className="h-4 w-4" /> Paramètres</Link>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className="gap-2 text-danger focus:bg-danger/10 focus:text-danger"
              onSelect={async () => {
                // Vidé avant le stockage : le persister ne réécrit pas les données dans localStorage
                queryClient.clear();
                await logout();
                window.location.href = '/login';
              }}
            >
              <LogOut className="h-4 w-4" /> Se déconnecter
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </header>
  );
};
